import dayjs from 'dayjs';
import { useAtomValue, useSetAtom } from 'jotai';
import { atomWithReset } from 'jotai/utils';
import { useEffect } from 'react';
import type { AdminsResponse } from 'apis/admins/admins_response_type';
import type { ContractsResponse } from 'apis/contracts/contracts_response_type';
import { getContractsApprovedFind } from 'apis/contracts/find';
import type { LicenseResponse } from 'apis/contracts/license_response_type';
import { getLicensesSearch } from 'apis/licenses/search';
import type { OrganizationAdminsMeResponse } from 'apis/organization_admins/me';
import type { OrganizationBillAddressResponse } from 'apis/organization_bill_addresses/organization_bill_addresses_response_type';
import { getOrganizationBillAddressesSearch } from 'apis/organization_bill_addresses/search';
import type { OrganizationsResponse } from 'apis/organizations/organizations_response_type';
import { getOrganizationsSearch } from 'apis/organizations/search';

// define atom's keys
export const currentAdminAtom = atomWithReset<
  Pick<AdminsResponse, 'id' | 'name' | 'email'>
>({
  id: 0,
  name: '',
  email: '',
});

export const currentOrganizationAdminAtom =
  atomWithReset<OrganizationAdminsMeResponse>({
    id: 0,
    adminId: 0,
    organizationId: 0,
    organizationName: '',
    status: '',
    logoUrl: null,
    permittedServices: {
      quest: false,
      competition: false,
      dxCopilotPlanner: false,
      dxCopilotAnalytics: false,
    },
  });

// for authorities
export const authoritiesAtom = atomWithReset<Map<string, boolean> | null>(null);

// for contracts
export const approvedContractsAtom = atomWithReset<ContractsResponse[] | null>(
  null
);
export const useSetApprovedContracts = () => {
  const approvedContracts = useAtomValue(approvedContractsAtom);
  const setApprovedContracts = useSetAtom(approvedContractsAtom);
  const currentOrganizationAdmin = useAtomValue(currentOrganizationAdminAtom);
  useEffect(() => {
    if (approvedContracts === null && currentOrganizationAdmin.id) {
      getContractsApprovedFind()
        .then((response) => {
          if (response.status === 200) {
            setApprovedContracts(response.data);
          }
        })
        .catch((_error) => {
          return;
        });
    }
  }, [approvedContracts, setApprovedContracts, currentOrganizationAdmin]);
};

// for organizations
export const organizationsAtom = atomWithReset<OrganizationsResponse | null>(
  null
);

export const useSetOrganizations = () => {
  const organizations = useAtomValue(organizationsAtom);
  const setOrganizations = useSetAtom(organizationsAtom);
  const currentOrganizationAdmin = useAtomValue(currentOrganizationAdminAtom);
  useEffect(() => {
    if (organizations === null && currentOrganizationAdmin.id) {
      getOrganizationsSearch()
        .then((response) => {
          if (response.status === 200) {
            setOrganizations(response.data);
          }
        })
        .catch((_error) => {
          return;
        });
    }
  }, [organizations, setOrganizations, currentOrganizationAdmin]);
};

// for organization bill addresses
export const organizationBillAddressesAtom =
  atomWithReset<OrganizationBillAddressResponse | null>(null);

export const useSetOrganizationBillAddresses = () => {
  const organizationBillAddresses = useAtomValue(organizationBillAddressesAtom);
  const setOrganizationBillAddresses = useSetAtom(
    organizationBillAddressesAtom
  );
  const currentOrganizationAdmin = useAtomValue(currentOrganizationAdminAtom);
  useEffect(() => {
    if (organizationBillAddresses === null && currentOrganizationAdmin.id) {
      getOrganizationBillAddressesSearch()
        .then((response) => {
          if (response.status === 200) {
            setOrganizationBillAddresses(response.data);
          }
        })
        .catch((_error) => {
          return;
        });
    }
  }, [
    organizationBillAddresses,
    setOrganizationBillAddresses,
    currentOrganizationAdmin,
  ]);
};

// for expired message
export const finishedAllLicensesAtom = atomWithReset<boolean | null>(null);
export const useSetFinishedAllLicenses = () => {
  const finishedAllLicenses = useAtomValue(finishedAllLicensesAtom);
  const setFinishedAllLicenses = useSetAtom(finishedAllLicensesAtom);
  const currentOrganizationAdmin = useAtomValue(currentOrganizationAdminAtom);
  useEffect(() => {
    if (finishedAllLicenses === null && currentOrganizationAdmin.id) {
      const now = dayjs();
      getLicensesSearch()
        .then((response) => {
          if (response.status === 200) {
            const allLicenses = response.data as Array<LicenseResponse>;
            setFinishedAllLicenses(
              !allLicenses.some((license) => {
                return now.isBefore(dayjs(license.endsAt));
              })
            );
          }
        })
        .catch((_error) => {
          return;
        });
    }
  }, [finishedAllLicenses, setFinishedAllLicenses, currentOrganizationAdmin]);
};
